import { mergeProps } from '@base-ui/react/merge-props';
import { useRender } from '@base-ui/react/use-render';
import { type VariantProps } from 'class-variance-authority';

import { cn } from '../../lib/utils';
import { Icon } from '../icon';
import type { IconName } from '../icon/iconMap';
import type { size as sizeVariants } from './variants';
import {
  badgeVariants,
  type BorderVariant,
  getDefaultBadgeIcon,
  type IconVariant,
  type NonBorderVariant,
} from './variants';

type Size = keyof typeof sizeVariants;

type BadgeVariantProps =
  | { variant?: BorderVariant; border?: boolean }
  | { variant?: NonBorderVariant; border?: false };

/**
 * `size="icon"` renders a single glyph, so one has to be resolvable: either the
 * variant carries a canonical default, or `icon` is supplied. Without this an
 * icon badge on a variant with no default would render an empty circle. Every
 * other size takes `icon` as an optional leading adornment.
 */
type BadgeSizeProps =
  | { size?: Exclude<Size, 'icon'>; icon?: IconName }
  | { size: 'icon'; variant?: IconVariant; icon?: IconName }
  | { size: 'icon'; icon: IconName };

type BadgeProps = useRender.ComponentProps<'span'> &
  Omit<VariantProps<typeof badgeVariants>, 'variant' | 'border' | 'size'> &
  BadgeVariantProps &
  BadgeSizeProps;

export const Badge = ({
  className,
  variant = 'success',
  size = 'md',
  border = false,
  icon,
  render,
  children,
  ...props
}: BadgeProps) => {
  // Icon-size badges render a single centred icon (formerly BadgeIcon). Use
  // the explicit `icon` if given, otherwise fall back to the variant default.
  const iconName =
    icon ?? (size === 'icon' ? getDefaultBadgeIcon(variant) : undefined);

  const label =
    typeof children === 'string' ? (
      <span className={cn('truncate')} title={children}>
        {children}
      </span>
    ) : (
      children
    );

  // An icon leads the label rather than replacing it — an icon-size badge just
  // has no label to render.
  const content = iconName ? (
    <>
      <Icon name={iconName} size={'xs'} />
      {label}
    </>
  ) : (
    label
  );

  return useRender({
    defaultTagName: 'span',
    props: mergeProps<'span'>(
      {
        className: cn(badgeVariants({ size, border, variant, className })),
        children: content,
      },
      props
    ),
    render,
    state: {
      slot: 'badge',
      variant,
    },
  });
};
